import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Users, FileText, CheckCircle, Clock, AlertCircle, Shield } from 'lucide-react';
import Header from '../components/Header';
import JudgmentMode from '../components/JudgmentMode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [diagnostics, setDiagnostics] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedDiagnostic, setSelectedDiagnostic] = useState(null);
  const [showJudgmentMode, setShowJudgmentMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingNotes, setEditingNotes] = useState({});
  const [pendingJudgmentCount, setPendingJudgmentCount] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [leadsRes, diagnosticsRes, pendingRes] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/diagnostics`),
        axios.get(`${API}/admin/diagnostics/pending-judgment`)
      ]);
      setLeads(leadsRes.data);
      setDiagnostics(diagnosticsRes.data);
      setPendingJudgmentCount(pendingRes.data.pending_count);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { status: newStatus });
      fetchData();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleNotesUpdate = async (leadId, notes) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { admin_notes: notes });
      setEditingNotes({ ...editingNotes, [leadId]: false });
      fetchData();
    } catch (err) {
      console.error('Error updating notes:', err);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new':
        return <AlertCircle className="w-4 h-4 text-[#F59E0B]" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-[#3B82F6]" />;
      case 'converted':
        return <CheckCircle className="w-4 h-4 text-[#10B981]" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    inProgress: leads.filter(l => l.status === 'in_progress').length,
    converted: leads.filter(l => l.status === 'converted').length,
    pendingJudgment: pendingJudgmentCount
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#FF6A00] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground" data-testid="admin-loading">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0D]">
      <Header />
      {/* Header */}
      <div className="bg-[#1A1A1D] border-b border-[#FF6A00]/10 pt-20">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <h1 className="text-3xl font-display font-bold" data-testid="admin-title">Operator Dashboard</h1>
          <p className="text-muted-foreground mt-1" data-testid="admin-subtitle">Manage leads and diagnostics</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="glass-card p-6 rounded-lg" data-testid="stat-total">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total Leads</span>
              <Users className="w-5 h-5 text-[#3B82F6]" />
            </div>
            <div className="text-3xl font-bold font-mono">{stats.total}</div>
          </div>

          <div className="glass-card p-6 rounded-lg" data-testid="stat-new">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">New</span>
              <AlertCircle className="w-5 h-5 text-[#FF6A00]" />
            </div>
            <div className="text-3xl font-bold font-mono">{stats.new}</div>
          </div>

          <div className="glass-card p-6 rounded-lg" data-testid="stat-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">In Progress</span>
              <Clock className="w-5 h-5 text-[#3B82F6]" />
            </div>
            <div className="text-3xl font-bold font-mono">{stats.inProgress}</div>
          </div>

          <div className="glass-card p-6 rounded-lg" data-testid="stat-converted">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Converted</span>
              <CheckCircle className="w-5 h-5 text-[#10B981]" />
            </div>
            <div className="text-3xl font-bold font-mono">{stats.converted}</div>
          </div>

          <div className="glass-card p-6 rounded-lg bg-[#FF6A00]/10 border-[#FF6A00]/30" data-testid="stat-judgment">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Pending Judgment</span>
              <Shield className="w-5 h-5 text-[#FF6A00]" />
            </div>
            <div className="text-3xl font-bold font-mono text-[#FF6A00]">{stats.pendingJudgment}</div>
          </div>
        </div>

        {/* Leads Table */}
        <div className="glass-card rounded-lg overflow-hidden">
          <div className="p-6 border-b border-border">
            <h2 className="text-xl font-display font-bold" data-testid="leads-table-title">All Leads</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="leads-table">
              <thead className="bg-muted/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Concern</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Judgment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {leads.map((lead) => {
                  const leadDiagnostic = diagnostics.find(d => d.lead_id === lead.id);
                  const judgmentStatus = leadDiagnostic?.judgment_status || 'N/A';
                  
                  return (
                    <tr key={lead.id} className="hover:bg-muted/20 transition-colors" data-testid={`lead-row-${lead.id}`}>
                      <td className="px-6 py-4 whitespace-nowrap" data-testid={`lead-name-${lead.id}`}>{lead.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap" data-testid={`lead-company-${lead.id}`}>{lead.company}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground" data-testid={`lead-email-${lead.id}`}>{lead.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" data-testid={`lead-type-${lead.id}`}>{lead.product_type}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" data-testid={`lead-concern-${lead.id}`}>{lead.primary_concern}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={lead.status}
                          onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                          className="text-sm bg-background border border-input rounded px-2 py-1"
                          data-testid={`lead-status-${lead.id}`}
                        >
                          <option value="new">New</option>
                          <option value="in_progress">In Progress</option>
                          <option value="converted">Converted</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {leadDiagnostic ? (
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              judgmentStatus === 'reviewed' 
                                ? 'bg-[#10B981]/20 text-[#10B981]' 
                                : 'bg-[#FF6A00]/20 text-[#FF6A00]'
                            }`}>
                              {judgmentStatus}
                            </span>
                            <button
                              onClick={() => {
                                setSelectedDiagnostic(leadDiagnostic.id);
                                setShowJudgmentMode(true);
                              }}
                              className="text-xs text-[#FF6A00] hover:text-[#FF6A00]/80"
                            >
                              Review
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-[#A0A0A0]">No diagnostic</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => setSelectedLead(lead)}
                          className="text-sm text-[#3B82F6] hover:text-[#3B82F6]/80"
                          data-testid={`lead-view-${lead.id}`}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Lead Details Modal */}
        {selectedLead && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedLead(null)}>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="glass-card p-8 rounded-lg max-w-2xl w-full"
              data-testid="lead-details-modal"
            >
              <h3 className="text-2xl font-display font-bold mb-4" data-testid="modal-lead-name">{selectedLead.name}</h3>
              <div className="space-y-3 mb-6">
                <div>
                  <span className="text-sm text-muted-foreground">Company:</span>
                  <p className="font-medium" data-testid="modal-lead-company">{selectedLead.company}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Email:</span>
                  <p className="font-medium" data-testid="modal-lead-email">{selectedLead.email}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Product Type:</span>
                  <p className="font-medium" data-testid="modal-lead-type">{selectedLead.product_type}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Primary Concern:</span>
                  <p className="font-medium" data-testid="modal-lead-concern">{selectedLead.primary_concern}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Admin Notes:</span>
                  <textarea
                    className="w-full mt-2 px-4 py-2 bg-background border border-input rounded-md"
                    rows={4}
                    defaultValue={selectedLead.admin_notes}
                    onChange={(e) => setEditingNotes({ ...editingNotes, [selectedLead.id]: e.target.value })}
                    data-testid="modal-lead-notes"
                  />
                  <button
                    onClick={() => handleNotesUpdate(selectedLead.id, editingNotes[selectedLead.id] || selectedLead.admin_notes)}
                    className="mt-2 px-4 py-2 bg-[#FF6A00] text-white rounded-md hover:bg-[#FF6A00]/90 transition-colors"
                    data-testid="save-notes-button"
                  >
                    Save Notes
                  </button>
                </div>
              </div>
              <button
                onClick={() => setSelectedLead(null)}
                className="px-6 py-2 bg-transparent border border-input rounded-md hover:bg-accent transition-colors"
                data-testid="close-modal-button"
              >
                Close
              </button>
            </motion.div>
          </div>
        )}
      </div>

      {/* Judgment Mode Modal */}
      {showJudgmentMode && selectedDiagnostic && (
        <JudgmentMode
          diagnosticId={selectedDiagnostic}
          onClose={() => {
            setShowJudgmentMode(false);
            setSelectedDiagnostic(null);
          }}
          onSubmit={() => {
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
